import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";

const MEDIA_TYPES_TEXTO_PLANO = ["conversation", "extendedTextMessage"];

const esTextoPlano = (m) => !m.mediaType || MEDIA_TYPES_TEXTO_PLANO.includes(m.mediaType);

const useStyles = makeStyles()((theme) => ({
  columns: {
    display: "flex",
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(1),
    alignItems: "flex-start",
  },
  leftCol: {
    flex: "1 1 55%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  rightCol: {
    flex: "1 1 45%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    height: 320,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(0.5),
    ...theme.scrollbarStyles,
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  itemText: {
    fontSize: "0.86rem",
    lineHeight: 1.4,
    paddingTop: 9,
  },
  itemTime: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    marginLeft: "auto",
    flexShrink: 0,
    paddingTop: 9,
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
  },
}));

const CreateTestCaseDialog = ({ open, onClose, ticketId, defaultTitle }) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [hasMore, setHasMore] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [expected, setExpected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTitle(defaultTitle || "");
    setCategory("");
    setExpected("");
    setMessages([]);
    setSelected(new Set());
    setLoading(true);

    (async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}`, { params: { pageNumber: 1 } });
        const delCliente = (data.messages || []).filter((m) => !m.fromMe && esTextoPlano(m) && m.body?.trim());
        setMessages(delCliente);
        setSelected(new Set(delCliente.map((m) => m.id)));
        setHasMore(Boolean(data.hasMore));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const seleccionados = messages.filter((m) => selected.has(m.id));

  const guardar = async () => {
    if (!title.trim() || !seleccionados.length) return;

    setSaving(true);
    try {
      await api.post("/test-scenarios", {
        title: title.trim(),
        category: category.trim(),
        messages: seleccionados.map((m) => m.body.trim()),
        expected: expected.trim(),
      });
      toast.success('Caso de prueba creado. Lo encontrás en Pruebas → "Casos de prueba".');
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crear caso de prueba desde esta conversación</DialogTitle>
      <DialogContent>
        <Box className={classes.columns}>
          <Box className={classes.leftCol}>
            <Typography variant="caption" color="textSecondary">
              Elegí qué mensajes del cliente entran al caso, en el orden en que los escribió.
              {hasMore && " Se muestran los últimos 20 mensajes del ticket."}
            </Typography>

            <Box className={classes.list}>
              {loading ? (
                <Typography className={classes.empty}>Cargando…</Typography>
              ) : messages.length === 0 ? (
                <Typography className={classes.empty}>
                  No hay mensajes de texto del cliente en este ticket para armar un caso.
                </Typography>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={classes.item} onClick={() => toggle(m.id)}>
                    <Checkbox
                      size="small"
                      checked={selected.has(m.id)}
                      onChange={() => toggle(m.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ padding: 4 }}
                    />
                    <Typography className={classes.itemText}>{m.body}</Typography>
                    <Typography className={classes.itemTime}>
                      {new Date(m.createdAt).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  </div>
                ))
              )}
            </Box>
          </Box>

          <Box className={classes.rightCol}>
            <TextField
              label="Título"
              fullWidth
              margin="dense"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Categoría (opcional)"
              fullWidth
              margin="dense"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <TextField
              label="Qué se espera que conteste la IA"
              fullWidth
              margin="dense"
              multiline
              minRows={6}
              placeholder="Ej: debería pedir la cédula una sola vez y no volver a preguntarla."
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={guardar}
          disabled={saving || loading || !title.trim() || !seleccionados.length}
        >
          Crear caso ({seleccionados.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTestCaseDialog;
